import React, { useState, useEffect, useCallback, useRef } from 'react';
import { usePriestAuth } from '../../context/PriestAuthContext';

const EMPTY = {
  yajamaniName: '', gotram: '', kulaDeity: '',
  purohitName: '', purohitPhone: '',
  dateOfBirth: '', timeOfBirth: '', placeOfBirth: '',
  gender: 'Male',
};

export default function PatrikaGenerator({ priest }) {
  const { priestFetch } = usePriestAuth();
  const [list, setList] = useState([]);
  const [form, setForm] = useState({
    ...EMPTY,
    purohitName: priest?.name || '',
    purohitPhone: priest?.phone || '',
    gotram: priest?.gotram || '',
    kulaDeity: priest?.kulaDeity || '',
  });
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const previewRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await priestFetch('/api/priest/patrikas');
      setList(data.patrikas || []);
    } catch (e) { /* */ }
  }, [priestFetch]);

  useEffect(() => { load(); }, [load]);

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const generate = (e) => {
    e.preventDefault();
    if (!form.yajamaniName.trim()) { setErr('Yajamani name is required'); return; }
    setErr('');
    setPreview(form);
  };

  const save = async () => {
    setSaving(true); setErr('');
    try {
      await priestFetch('/api/priest/patrikas', {
        method: 'POST',
        body: JSON.stringify({ ...form, data: form }),
      });
      setForm({ ...EMPTY,
        purohitName: priest?.name || '', purohitPhone: priest?.phone || '',
        gotram: priest?.gotram || '', kulaDeity: priest?.kulaDeity || '' });
      setPreview(null);
      await load();
      alert('✅ Patrika saved');
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const print = () => {
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Janma Patrika - ${preview.yajamaniName}</title>
      <style>${PRINT_CSS}</style></head><body>${previewRef.current.innerHTML}</body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 250);
  };

  const shareWhatsApp = () => {
    const text = encodeURIComponent(
`🕉️ *Janma Patrika*
Name: ${preview.yajamaniName}
Gotram: ${preview.gotram}
Kula Deity: ${preview.kulaDeity}
DOB: ${preview.dateOfBirth}
Time: ${preview.timeOfBirth}
Place: ${preview.placeOfBirth}
Purohit: ${preview.purohitName}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this patrika?')) return;
    try {
      await priestFetch(`/api/priest/patrikas/${id}`, { method: 'DELETE' });
      load();
    } catch (e) { alert(e.message); }
  };

  return (
    <div className="pg-wrapper">
      <div className="mc-grid">
        <div className="mc-form-card">
          <h3>📜 Generate Janma Patrika</h3>
          {err && <div className="auth-error">⚠️ {err}</div>}

          <form onSubmit={generate}>
            <div className="form-group">
              <label>Yajamani Name *</label>
              <input value={form.yajamaniName}
                onChange={e => update('yajamaniName', e.target.value)} />
            </div>
            <div className="form-row-2">
              <div className="form-group">
                <label>Gotram</label>
                <input value={form.gotram} onChange={e => update('gotram', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Kula Deity</label>
                <input value={form.kulaDeity} onChange={e => update('kulaDeity', e.target.value)} />
              </div>
            </div>
            <div className="form-row-2">
              <div className="form-group">
                <label>Date of Birth</label>
                <input type="date" value={form.dateOfBirth}
                  onChange={e => update('dateOfBirth', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Time of Birth</label>
                <input type="time" value={form.timeOfBirth}
                  onChange={e => update('timeOfBirth', e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label>Place of Birth</label>
              <input value={form.placeOfBirth}
                onChange={e => update('placeOfBirth', e.target.value)} />
            </div>
            <div className="form-row-2">
              <div className="form-group">
                <label>Gender</label>
                <select value={form.gender} onChange={e => update('gender', e.target.value)}>
                  <option>Male</option><option>Female</option><option>Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Purohit Name</label>
                <input value={form.purohitName} onChange={e => update('purohitName', e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label>Purohit Phone</label>
              <input value={form.purohitPhone} onChange={e => update('purohitPhone', e.target.value)} />
            </div>

            <button type="submit" className="btn btn-primary">🔍 Preview Patrika</button>
          </form>
        </div>

        <div className="mc-list-card">
          <h3>📚 Saved Patrikas</h3>
          {list.length === 0 ? (
            <p className="bm-empty-sm">No patrikas saved yet.</p>
          ) : (
            <div className="mc-list">
              {list.map(p => (
                <div key={p.id} className="mc-item">
                  <strong>{p.yajamaniName}</strong>
                  <div className="mc-item-meta">📅 {p.dateOfBirth || 'N/A'} • 📍 {p.placeOfBirth || 'N/A'}</div>
                  <div className="mc-item-meta">Gotram: {p.gotram || '—'}</div>
                  <div className="mc-item-actions">
                    <button className="bm-btn bm-btn-edit"
                      onClick={() => setPreview(p)}>👁️ View</button>
                    <button className="bm-btn bm-btn-del" onClick={() => remove(p.id)}>🗑️</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {preview && (
        <div className="pg-preview-overlay" onClick={() => setPreview(null)}>
          <div className="pg-preview-modal" onClick={e => e.stopPropagation()}>
            <div className="pg-preview-actions">
              <button className="bm-btn bm-btn-edit" onClick={print}>🖨️ Print / PDF</button>
              <button className="bm-btn bm-btn-msg" onClick={shareWhatsApp}>📤 WhatsApp</button>
              <button className="bm-btn bm-btn-done" onClick={save} disabled={saving}>
                {saving ? 'Saving...' : '💾 Save'}
              </button>
              <button className="bm-btn bm-btn-del" onClick={() => setPreview(null)}>✕</button>
            </div>

            <div ref={previewRef} className="pg-patrika">
              <div className="pg-patrika-border">
                <h1 className="pg-patrika-title">🕉️ श्री गणेशाय नमः 🕉️</h1>
                <h2 className="pg-patrika-sub">॥ जन्म पत्रिका ॥</h2>

                <table className="pg-patrika-table">
                  <tbody>
                    <Row label="Name (नाम)"             value={preview.yajamaniName} />
                    <Row label="Gender (लिंग)"          value={preview.gender} />
                    <Row label="Gotram (गोत्र)"         value={preview.gotram} />
                    <Row label="Kula Deity (कुल देवता)" value={preview.kulaDeity} />
                    <Row label="Date of Birth"          value={preview.dateOfBirth} />
                    <Row label="Time of Birth"          value={preview.timeOfBirth} />
                    <Row label="Place of Birth"         value={preview.placeOfBirth} />
                  </tbody>
                </table>

                <div className="pg-patrika-footer">
                  <p>Prepared by</p>
                  <p className="pg-purohit-name">{preview.purohitName || '—'}</p>
                  <p>{preview.purohitPhone}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <tr>
      <th>{label}</th>
      <td>{value || '—'}</td>
    </tr>
  );
}

const PRINT_CSS = `
  body { font-family: serif; padding: 40px; color: #2a1215; }
  .pg-patrika-border { border: 3px double #d4a017; padding: 30px; }
  .pg-patrika-title { text-align: center; color: #c2185b; }
  .pg-patrika-sub { text-align: center; color: #d4a017; margin-bottom: 30px; }
  .pg-patrika-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
  .pg-patrika-table th, .pg-patrika-table td { padding: 10px; border-bottom: 1px solid #ddd; text-align: left; }
  .pg-patrika-table th { width: 40%; color: #800020; }
  .pg-patrika-footer { text-align: center; margin-top: 40px; }
  .pg-purohit-name { font-weight: bold; font-size: 1.2em; color: #c2185b; }
`;
